import styled from 'styled-components';
import {DatePicker, Select, Space} from 'antd';
import DebouncedInput from '../../../DebouncedInput';
import DeleteModal from '../../../DeleteModal';
import Tabs from '../../../Tabs';
import {iconColor, panelTabsStyles, tableStyles, typographyStyles as fontStyles} from '../../../../utils/styles';

const StyledJournalEntriesTabsContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 66px);
  overflow-x: hidden;
  padding: 24px;
  position: relative;
  transition: .3s ease-in-out;

  .accounting-details-panel {
    & > div {
      position: sticky;
      top: 0;
      transition: .2s ease-in-out;
    }
  }

  .je-expense-details-panel {
    & > {
      div {
        padding-left: 24px;
      }
    }
  }

  .accounting-details-content {
    &:has(> div:nth-child(3)) > div:nth-child(2) {
      max-height: calc(100vh - (66px + 24px * 2 + 56px) - 73px);
      transition: .2s ease-in-out;
    }
  }
`;

const StyledJournalEntriesTabs = styled(Tabs)`
  ${panelTabsStyles};
`;


const StyledJournalEntriesTabsIconSpace = styled(Space)`
  display: flex;

  &:not(.justify-start) {
    justify-content: center;
  }

  & > .ant-space-item {
    display: flex;
  }

  svg {
    height: 16px;
    width: 16px;

    ${iconColor(props => props.theme.colors.neutral_6)}

    &.expense-type-icon {
      &[fill] {
        rect {
          & + path {
            stroke: ${props => props.theme.colors.neutral_1} !important;
            fill: ${props => props.theme.colors.neutral_1} !important;
          }
        }
      }
    }
  }
`;

const StyledJournalEntriesTabsInput = styled(DebouncedInput)`
  min-width: 100px;
  width: max-content;
  padding-left: 0;
`;

const StyledJournalEntriesTabsDatePicker = styled(DatePicker)`
  min-width: 100px;
  padding-left: 0;

  &.ant-picker {
    padding-left: 0;
  }
`;

const StyledJournalEntriesTabsSelect = styled(Select)`
  flex: 1;
  min-width: 100px;
  max-width: 150px;

  &.ant-select-single:not(.ant-select-customize-input) {
    .ant-select-selector {
      padding-left: 0;

      .ant-select-selection-search {
        inset-inline-start: 0;
      }
    }
  }
`;

const StyledJournalEntryTabContainer = styled(Space)`
  position: relative;
  width: 100%;

  ${tableStyles.cellActionsHover}

  ${tableStyles.fullColumnHeadings}

  .ant-table-wrapper .ant-table-selection-column {
    padding-inline-end: 16px;
    padding-inline-start: 16px;
  }

  .ant-table-expanded-row-fixed {
    padding-left: 0 !important;
  }

  .ant-table-row-expand-icon-cell {
    padding: 0 !important;
  }

  .je-multi-invoice-row > td {
    background-color: #FFFAEB !important;
  }

  .ant-table-expanded-row.je-multi-invoice-expanded > td {
    background-color: #FFFAEB;
    padding: 0 !important;
  }

  .je-split-group-header {
    & > td {
      background-color: ${props => props.theme.colors.neutral_2} !important;
      /* Padding lives on the sticky label (StyledSplitGroupHeader) so the label
         fills the row height and its accent border stays pinned on scroll. */
      padding: 0 !important;
      pointer-events: none;
      /* Neutralize .ant-table-cell-ellipsis overflow:hidden so the sticky
         group-header label resolves against .ant-table-body, not the cell. */
      overflow: visible !important;
    }
    .ant-table-selection-column {
      display: none;
    }
  }

  .je-split-group-item {
    & > td {
      background-color: #FAFAFB !important;
    }
  }

  /* Group-item accent border rides the fixed-left selection column, so it stays
     pinned. The header's border is applied to the sticky label instead (see
     StyledSplitGroupHeader) because its full-width cell scrolls away. */
  .je-split-group-item .ant-table-selection-column {
    box-shadow: inset 2px 0 0 ${props => props.theme.colors.neutral_8} !important;
  }

  .je-split-group-header {
    background-color: #FAFAFB !important;

    .ant-table-cell-ellipsis {
      left: 0 !important;
    }
    & > .ant-table-cell {
      &:first-of-type {
        display: none;
      }
    }
  }

  .je-split-group-item {
    background: #fafafa;

    &.ant-table-row-selected {
      & > .ant-table-cell {
        background-color: #f0feff !important;
      }
    }
  }

  .je-split-group-header-selected {
    & > td {
      background-color: #f0feff !important;
    }
  }
`;

const StyledMultiInvoiceBanner = styled.div`
  align-items: center;
  background-color: #FFFAEB;
  display: flex;
  gap: 8px;
  padding: 8px 16px;
  ${fontStyles.footnote.description};
  color: ${props => props.theme.colors.neutral_10};
  width: 100%;

  .ant-btn {
    display: flex;
    ${fontStyles.body.regular};

    &:hover, &:focus {
      svg {
        path {
          stroke: ${props => props.theme.colors.primary_8};
        }
      }
    }
    .ant-btn-icon {
      display: flex;
    }
  }
`;

const StyledJournalEntrySyncSpace = styled(Space)`
  & > .ant-space-item {
    align-items: center;
    display: flex;
  }
`;

const StyledAmountDropdownContent = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
  padding: 8px 12px;
`;

const StyledAmountInputRow = styled(Space.Compact)`
  width: 100%;
`;

const StyledAmountError = styled.div`
  color: ${props => props.theme.colors.functional_danger};
  font-size: 12px;
  margin-top: 4px;
`;

const StyledJournalEntriesTabsDeleteModal = styled(DeleteModal)`
  .ant-modal-body {
    p {
      max-width: 300px;
    }
  }
`;

export {
  StyledAmountDropdownContent,
  StyledAmountError,
  StyledAmountInputRow,
  StyledJournalEntriesTabs,
  StyledJournalEntriesTabsContainer,
  StyledJournalEntriesTabsDatePicker,
  StyledJournalEntriesTabsDeleteModal,
  StyledJournalEntriesTabsIconSpace,
  StyledJournalEntriesTabsInput,
  StyledJournalEntriesTabsSelect,
  StyledJournalEntryTabContainer,
  StyledJournalEntrySyncSpace,
  StyledMultiInvoiceBanner,
}
